/**
 * 进行中的任务。来自台账,进度是**数出来的**(已完成单元 / 可完成单元),
 * 不是任务行自报的一个 progress 字段 —— 自报的进度和实际执行没有强制关系。
 */
import { useNavigate } from 'react-router-dom';
import type { Task, TaskUnit } from '@/types/fleet';
import { NA } from '@/lib/format';

const statusLabel: Record<string, string> = {
  pending: '待启动', in_progress: '进行中', completed: '已完成', failed: '已失败', cancelled: '已取消',
};
const statusStyle: Record<string, string> = {
  pending: 'bg-secondary-100 text-secondary-700', in_progress: 'bg-accent-100 text-accent-700',
  completed: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-background-200 text-foreground-500',
};

export default function ActiveTasks({ tasks, units, loading }: { tasks: Task[]; units: TaskUnit[]; loading: boolean }) {
  const navigate = useNavigate();
  const active = tasks.filter((t) => t.status === 'in_progress' || t.status === 'pending').slice(0, 6);
  const unclaimed = units.filter((u) => u.status === 'unclaimed').length;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 h-full">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground-800 font-heading">进行中的任务</h3>
        <button onClick={() => navigate('/tasks')} className="text-[11px] text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">
          全部任务 <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      {loading && tasks.length === 0 ? (
        <p className="text-xs text-foreground-400 py-6 text-center">读取中…</p>
      ) : active.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-sm text-foreground-500">没有进行中的任务</p>
          <p className="text-[11px] text-foreground-400 mt-1">
            台账里共 {tasks.length} 个任务{unclaimed > 0 ? `,${unclaimed} 个单元待认领` : ''}。
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {active.map((t) => (
            <div key={t.id} onClick={() => navigate('/tasks')}
              className="p-3 rounded-lg bg-background-100/70 hover:bg-background-100 transition-colors cursor-pointer">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-sm font-medium text-foreground-800 truncate flex-1">{t.name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${statusStyle[t.status]}`}>{statusLabel[t.status]}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-background-200 overflow-hidden">
                  <div className={`h-full rounded-full ${t.progress === 100 ? 'bg-green-500' : 'bg-accent-500'}`} style={{ width: `${t.progress}%` }} />
                </div>
                <span className="text-[11px] font-mono text-foreground-600 w-9 text-right">{t.progress}%</span>
              </div>
              <div className="text-[10px] text-foreground-400 mt-1">
                认领:{t.assignees.length ? t.assignees.join('、') : '尚无'} · 创建 {t.created ? new Date(t.created).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : NA}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
